/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import { Component, inject } from "@angular/core";
import { EChartsType } from "echarts";
import { defer, map, Observable, Subject } from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { ChartComponent } from "../../../../shared/components/chart/chart.component";
import { BarChart } from "../../../../shared/models/http/charts/bar-chart/bar-chart.model";
import { ChartResponse } from "../../../../shared/models/ui/charts.model";
import { CloudAssetsGlobalForm } from "../../../../shared/models/ui/cloud-assets-global-form.model";
import { AuthorizationService } from "../../../../shared/services/authorization.service";
import { DashboardCloudService } from "../../../../shared/services/dashboard-cloud.service";
import { DashboardStateService } from "../../../../shared/services/state/dashboard-state.service";
import { StorageService } from "../../../../shared/services/storage.service";
import { GRANTS } from "../../../../shared/utils/grants.constants";
import { lazyLoad, Loadable } from "../../../../shared/utils/lazy-load";
import { formatDateForBE, saveAs } from "../../../../shared/utils/utils";

type CloudEnvironmentalImpactFilters = {
	tenantId: string;
	cloudProviders: string[];
	applications: string[];
	locations: string[];
	startDate: string;
	endDate: string;
};

@Component({
	selector: "globalictcc-cloud-environmental-impact",
	standalone: true,
	imports: [AsyncPipe, ChartComponent, ButtonComponent],
	templateUrl: "./cloud-environmental-impact.component.html",
	styleUrl: "./cloud-environmental-impact.component.scss"
})
export class CloudEnvironmentalImpactComponent {
	private readonly _document: Document = inject(DOCUMENT);
	public readonly grants = GRANTS.cloud;

	private readonly _worst10ApplicationsReload$ = new Subject<void>();
	private readonly _worst10LocationsReload$ = new Subject<void>();

	private readonly _filters$: Observable<CloudEnvironmentalImpactFilters | undefined> = defer(
		() => this._dashboardStateService.cloudAssetsGlobalForm$
	).pipe(map(form => this._buildFilters(form)));

	// Streams are cold: an API is only called while a rendered widget subscribes to it.
	public readonly worst10Applications$: Observable<Loadable<ChartResponse<BarChart>>> = lazyLoad(
		this._filters$,
		filters =>
			this._dashboardCloudService.getWorst10Applications(
				filters.tenantId,
				filters.cloudProviders,
				filters.applications,
				filters.locations,
				filters.startDate,
				filters.endDate
			),
		this._worst10ApplicationsReload$
	);

	public readonly worst10Locations$: Observable<Loadable<ChartResponse<BarChart>>> = lazyLoad(
		this._filters$,
		filters =>
			this._dashboardCloudService.getWorst10Locations(
				filters.tenantId,
				filters.cloudProviders,
				filters.applications,
				filters.locations,
				filters.startDate,
				filters.endDate
			),
		this._worst10LocationsReload$
	);

	constructor(
		private _dashboardCloudService: DashboardCloudService,
		private _storageService: StorageService,
		private _dashboardStateService: DashboardStateService,
		public authorizationService: AuthorizationService
	) {}

	/**
	 * Refreshes the worst 10 applications chart with the current filters.
	 */
	public onWorst10ApplicationsChartRefresh() {
		this._worst10ApplicationsReload$.next();
	}

	/**
	 * Refreshes the worst 10 locations chart with the current filters.
	 */
	public onWorst10LocationsChartRefresh() {
		this._worst10LocationsReload$.next();
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	/**
	 * Maps the shared cloud form to the request filters.
	 * @returns The filters, or `undefined` when the time period is not valid.
	 */
	private _buildFilters(
		form?: CloudAssetsGlobalForm
	): CloudEnvironmentalImpactFilters | undefined {
		const startDate = formatDateForBE(form?.timePeriod?.start);
		const endDate = formatDateForBE(form?.timePeriod?.end);

		if (!form || !startDate || !endDate) return undefined;

		return {
			tenantId: this._storageService.getTenantId(),
			cloudProviders: form.cloudProviders,
			applications: form.applications,
			locations: form.locations,
			startDate,
			endDate
		};
	}
}
